import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';

interface MatchingField {
  fieldName: string;
  weight: number;
  matchType: 'exact' | 'fuzzy' | 'phonetic' | 'numeric' | 'date' | 'phone' | 'email';
  threshold?: number;
}

interface MatchingConfig {
  fields: MatchingField[];
  minimumConfidence: number;
  createNewCustomers: boolean;
  fieldMappings: Record<string, string>;
  sheetNames?: string[];
}

interface Customer {
  id: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  createdAt: string;
  updatedAt: string;
  customerRecords: CustomerRecord[];
  shipments: any[];
  phoneCallLogs: PhoneCallLog[];
}

interface CustomerRecord {
  id: string;
  sourceFile: string;
  sourceSheet?: string;
  sourceRowIndex?: number;
  matchConfidence: number;
  matchedFields: string[];
  createdAt: string;
}

interface PhoneCallLog {
  id: string;
  phoneNumber: string;
  direction: 'inbound' | 'outbound';
  duration?: number;
  status?: string;
  notes?: string;
  calledAt: string;
}

interface SheetInfo {
  name: string;
  columns: string[];
  rowCount: number;
}

interface MatchingJob {
  id: string;
  status: string;
  totalRecords?: number;
  processedRecords?: number;
  matchedRecords?: number;
  newCustomers?: number;
  errorMessage?: string;
  startedAt?: string;
  completedAt?: string;
}

const RecordMatchingPage: React.FC = () => {
  const [files, setFiles] = useState<FileList | null>(null);
  const [sheets, setSheets] = useState<SheetInfo[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [suggestedMappings, setSuggestedMappings] = useState<Record<string, string>>({});
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>({});
  const [matchingFields, setMatchingFields] = useState<MatchingField[]>([
    { fieldName: 'phoneNumber', weight: 0.4, matchType: 'phone', threshold: 1.0 },
    { fieldName: 'email', weight: 0.3, matchType: 'email', threshold: 1.0 },
    { fieldName: 'lastName', weight: 0.2, matchType: 'fuzzy', threshold: 0.8 },
    { fieldName: 'zipCode', weight: 0.1, matchType: 'exact', threshold: 1.0 },
  ]);
  const [minimumConfidence, setMinimumConfidence] = useState(0.7);
  const [createNewCustomers, setCreateNewCustomers] = useState(true);
  const [selectedSheets, setSelectedSheets] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentJob, setCurrentJob] = useState<MatchingJob | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState<any>(null);

  const customerFields = [
    'firstName', 'lastName', 'fullName', 'email', 'phoneNumber',
    'address', 'city', 'state', 'zipCode', 'country', 'dateOfBirth'
  ];

  const matchTypes = ['exact', 'fuzzy', 'phonetic', 'numeric', 'date', 'phone', 'email'];

  useEffect(() => {
    loadStats();
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(apiUrl('/api/record-matching/stats'));
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await axios.get(apiUrl('/api/record-matching/customers'), {
        params: { limit: 50, search: searchTerm }
      });
      setCustomers(response.data.customers);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    setFiles(selectedFiles);
    
    if (selectedFiles && selectedFiles.length > 0) {
      // Analyze first file to get column suggestions
      const formData = new FormData();
      formData.append('file', selectedFiles[0]);
      
      try {
        const response = await axios.post(apiUrl('/api/record-matching/suggest-mappings'), formData);
        setSheets(response.data.sheets);
        setSuggestedMappings(response.data.suggestedMappings);
        setFieldMappings(response.data.suggestedMappings);
        
        // Select all sheets by default
        setSelectedSheets(response.data.sheets.map((sheet: SheetInfo) => sheet.name));
      } catch (error) {
        console.error('Failed to analyze file:', error);
      }
    }
  };

  const handleMatchingFieldChange = (index: number, field: Partial<MatchingField>) => {
    const newFields = [...matchingFields];
    newFields[index] = { ...newFields[index], ...field };
    setMatchingFields(newFields);
  };

  const addMatchingField = () => {
    setMatchingFields([
      ...matchingFields,
      { fieldName: 'firstName', weight: 0.1, matchType: 'fuzzy', threshold: 0.8 }
    ]);
  };

  const removeMatchingField = (index: number) => {
    setMatchingFields(matchingFields.filter((_, i) => i !== index));
  };

  const handleStartMatching = async () => {
    if (!files || files.length === 0) {
      alert('Please select Excel files to process');
      return;
    }

    const config: MatchingConfig = {
      fields: matchingFields,
      minimumConfidence,
      createNewCustomers,
      fieldMappings,
      sheetNames: selectedSheets.length > 0 ? selectedSheets : undefined,
    };

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }
    formData.append('config', JSON.stringify(config));

    setIsProcessing(true);
    try {
      const response = await axios.post(apiUrl('/api/record-matching/match'), formData);
      setCurrentJob({ id: response.data.jobId, status: 'completed', ...response.data });
      
      // Refresh customers and stats
      await loadCustomers();
      await loadStats();
      
      alert(`Matching completed! Processed ${response.data.totalRecords} records, matched ${response.data.matchedRecords}, created ${response.data.newCustomers} new customers.`);
    } catch (error) {
      console.error('Matching failed:', error);
      alert('Matching failed. Please check the console for details.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCustomerClick = async (customer: Customer) => {
    try {
      const response = await axios.get(apiUrl(`/api/record-matching/customers/${customer.id}`));
      setSelectedCustomer(response.data);
    } catch (error) {
      console.error('Failed to load customer details:', error);
    }
  };

  const handleAddPhoneCall = async (customerId: string, phoneNumber: string) => {
    const direction = prompt('Call direction (inbound/outbound):');
    const duration = prompt('Duration in seconds (optional):');
    const status = prompt('Call status (optional):');
    const notes = prompt('Notes (optional):');

    if (!direction || !['inbound', 'outbound'].includes(direction)) {
      alert('Please specify valid direction (inbound or outbound)');
      return;
    }

    try {
      await axios.post(apiUrl('/api/record-matching/phone-calls'), {
        customerId,
        phoneNumber,
        direction,
        duration: duration ? parseInt(duration) : undefined,
        status: status || undefined,
        notes: notes || undefined,
      });

      // Refresh customer details
      if (selectedCustomer?.id === customerId) {
        handleCustomerClick(selectedCustomer);
      }
    } catch (error) {
      console.error('Failed to add phone call:', error);
      alert('Failed to add phone call');
    }
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Record Matching</h1>

      {/* Statistics Dashboard */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-blue-100 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-800">Total Customers</h3>
            <p className="text-2xl font-bold text-blue-600">{stats.totalCustomers}</p>
          </div>
          <div className="bg-green-100 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-green-800">Total Records</h3>
            <p className="text-2xl font-bold text-green-600">{stats.totalRecords}</p>
          </div>
          <div className="bg-purple-100 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-purple-800">Phone Calls</h3>
            <p className="text-2xl font-bold text-purple-600">{stats.totalPhoneCalls}</p>
          </div>
          <div className="bg-orange-100 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-orange-800">Linked Shipments</h3>
            <p className="text-2xl font-bold text-orange-600">{stats.totalShipments}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column: File Upload and Configuration */}
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Upload Excel Files</h2>
            <input
              type="file"
              multiple
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="w-full p-2 border border-gray-300 rounded"
              aria-label="Upload excel files"
            />
            
            {sheets.length > 0 && (
              <div className="mt-4">
                <h3 className="font-semibold mb-2">Available Sheets:</h3>
                {sheets.map((sheet) => (
                  <label key={sheet.name} className="flex items-center space-x-2 mb-2">
                    <input
                      type="checkbox"
                      checked={selectedSheets.includes(sheet.name)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedSheets([...selectedSheets, sheet.name]);
                        } else {
                          setSelectedSheets(selectedSheets.filter(s => s !== sheet.name));
                        }
                      }}
                    />
                    <span>{sheet.name} ({sheet.rowCount} rows, {sheet.columns.length} columns)</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Field Mappings */}
          {sheets.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Field Mappings</h2>
              <div className="space-y-3">
                {customerFields.map((customerField) => (
                  <div key={customerField} className="flex items-center space-x-3">
                    <label className="w-32 text-sm font-medium">{customerField}:</label>
                    <select
                      value={Object.keys(fieldMappings).find(k => fieldMappings[k] === customerField) || ''}
                      onChange={(e) => {
                        const newMappings = { ...fieldMappings };
                        // Remove old mapping
                        Object.keys(newMappings).forEach(key => {
                          if (newMappings[key] === customerField) {
                            delete newMappings[key];
                          }
                        });
                        // Add new mapping
                        if (e.target.value) {
                          newMappings[e.target.value] = customerField;
                        }
                        setFieldMappings(newMappings);
                      }}
                      className="flex-1 p-2 border border-gray-300 rounded text-sm"
                    >
                      <option value="">-- Select Column --</option>
                      {sheets.flatMap(sheet => sheet.columns).map((column, index) => (
                        <option key={index} value={column}>{column}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Matching Configuration */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Matching Configuration</h2>
            
            <div className="mb-4">
              <label htmlFor="matching-min-confidence" className="block text-sm font-medium mb-2">
                Minimum Confidence: {minimumConfidence.toFixed(2)}
              </label>
              <input
                id="matching-min-confidence"
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={minimumConfidence}
                onChange={(e) => setMinimumConfidence(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>

            <div className="mb-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={createNewCustomers}
                  onChange={(e) => setCreateNewCustomers(e.target.checked)}
                />
                <span>Create new customers for unmatched records</span>
              </label>
            </div>

            <div className="mb-4">
              <h3 className="font-semibold mb-2">Matching Fields</h3>
              {matchingFields.map((field, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2 p-2 border rounded">
                  <select
                    value={field.fieldName}
                    onChange={(e) => handleMatchingFieldChange(index, { fieldName: e.target.value })}
                    className="p-1 border rounded text-sm"
                  >
                    {customerFields.map(cf => (
                      <option key={cf} value={cf}>{cf}</option>
                    ))}
                  </select>
                  
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={field.weight}
                    onChange={(e) => handleMatchingFieldChange(index, { weight: parseFloat(e.target.value) })}
                    className="w-16 p-1 border rounded text-sm"
                    placeholder="Weight"
                  />
                  
                  <select
                    value={field.matchType}
                    onChange={(e) => handleMatchingFieldChange(index, { matchType: e.target.value as any })}
                    className="p-1 border rounded text-sm"
                  >
                    {matchTypes.map(mt => (
                      <option key={mt} value={mt}>{mt}</option>
                    ))}
                  </select>
                  
                  {field.matchType === 'fuzzy' && (
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.1"
                      value={field.threshold || 0.8}
                      onChange={(e) => handleMatchingFieldChange(index, { threshold: parseFloat(e.target.value) })}
                      className="w-16 p-1 border rounded text-sm"
                      placeholder="Threshold"
                    />
                  )}
                  
                  <button
                    onClick={() => removeMatchingField(index)}
                    className="px-2 py-1 bg-red-500 text-white rounded text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
              
              <button
                onClick={addMatchingField}
                className="px-3 py-1 bg-green-500 text-white rounded text-sm"
              >
                Add Field
              </button>
            </div>

            <button
              onClick={handleStartMatching}
              disabled={isProcessing || !files}
              className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400"
            >
              {isProcessing ? 'Processing...' : 'Start Matching'}
            </button>
          </div>
        </div>

        {/* Right Column: Results and Customer Management */}
        <div className="space-y-6">
          {/* Customer Search and List */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Customers</h2>
            
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
              />
              <button
                onClick={loadCustomers}
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Search
              </button>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {customers.map((customer) => (
                <div
                  key={customer.id}
                  onClick={() => handleCustomerClick(customer)}
                  className="p-3 border-b cursor-pointer hover:bg-gray-50"
                >
                  <div className="font-semibold">
                    {customer.fullName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Unknown'}
                  </div>
                  <div className="text-sm text-gray-600">
                    {customer.email && <span>{customer.email} • </span>}
                    {customer.phoneNumber && <span>{customer.phoneNumber} • </span>}
                    <span>{customer.customerRecords.length} records</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Selected Customer Details */}
          {selectedCustomer && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Customer Details</h2>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <strong>Name:</strong> {selectedCustomer.fullName || `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim() || 'N/A'}
                </div>
                <div>
                  <strong>Email:</strong> {selectedCustomer.email || 'N/A'}
                </div>
                <div>
                  <strong>Phone:</strong> {selectedCustomer.phoneNumber || 'N/A'}
                  {selectedCustomer.phoneNumber && (
                    <button
                      onClick={() => handleAddPhoneCall(selectedCustomer.id, selectedCustomer.phoneNumber!)}
                      className="ml-2 px-2 py-1 bg-green-500 text-white rounded text-xs"
                    >
                      Add Call
                    </button>
                  )}
                </div>
                <div>
                  <strong>Address:</strong> {[selectedCustomer.address, selectedCustomer.city, selectedCustomer.state, selectedCustomer.zipCode].filter(Boolean).join(', ') || 'N/A'}
                </div>
              </div>

              {/* Customer Records */}
              <div className="mb-4">
                <h3 className="font-semibold mb-2">Source Records ({selectedCustomer.customerRecords.length})</h3>
                <div className="max-h-32 overflow-y-auto">
                  {selectedCustomer.customerRecords.map((record) => (
                    <div key={record.id} className="text-sm p-2 border-b">
                      <span className="font-medium">{record.sourceFile}</span>
                      {record.sourceSheet && <span> • {record.sourceSheet}</span>}
                      <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                        {(record.matchConfidence * 100).toFixed(1)}% confidence
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Phone Call Logs */}
              <div className="mb-4">
                <h3 className="font-semibold mb-2">Phone Calls ({selectedCustomer.phoneCallLogs.length})</h3>
                <div className="max-h-32 overflow-y-auto">
                  {selectedCustomer.phoneCallLogs.map((call) => (
                    <div key={call.id} className="text-sm p-2 border-b">
                      <span className="font-medium">{call.direction}</span> • {call.phoneNumber}
                      {call.duration && <span> • {call.duration}s</span>}
                      {call.status && <span> • {call.status}</span>}
                      <div className="text-gray-500">{new Date(call.calledAt).toLocaleString()}</div>
                      {call.notes && <div className="text-gray-600 italic">{call.notes}</div>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipments */}
              <div>
                <h3 className="font-semibold mb-2">Shipments ({selectedCustomer.shipments.length})</h3>
                <div className="max-h-32 overflow-y-auto">
                  {selectedCustomer.shipments.map((shipment) => (
                    <div key={shipment.id} className="text-sm p-2 border-b">
                      <span className="font-medium">{shipment.trackingNumber || shipment.externalId}</span>
                      {shipment.status && <span> • {shipment.status}</span>}
                      {shipment.shippedAt && (
                        <div className="text-gray-500">{new Date(shipment.shippedAt).toLocaleDateString()}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecordMatchingPage;















